import React, { useState, useRef } from "react";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import "./ChartBlock.css";

const COLORS = [
  "#6366f1", "#22d3ee", "#f59e0b", "#ef4444", "#10b981",
  "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#84cc16",
  "#06b6d4", "#a855f7", "#e11d48", "#0ea5e9", "#d97706",
  "#16a34a", "#7c3aed", "#db2777", "#0891b2", "#65a30d",
];

const CHART_ICONS = {
  bar: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="12" width="4" height="9" rx="1" /><rect x="10" y="7" width="4" height="14" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" />
    </svg>
  ),
  pie: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" />
    </svg>
  ),
  line: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
};

// Custom legend for pie charts — renders below the chart, wraps cleanly
function PieLegend({ data, x_axis, colors }) {
  return (
    <div className="pie-legend">
      {data.map((entry, i) => (
        <div key={i} className="pie-legend-item" title={entry[x_axis]}>
          <span className="pie-legend-dot" style={{ background: colors[i % colors.length] }} />
          <span className="pie-legend-label">{entry[x_axis]}</span>
        </div>
      ))}
    </div>
  );
}

function ChartBlock({ chart, columns, rows }) {
  const [activeType, setActiveType] = useState(chart?.type || "bar");
  const chartRef = useRef(null);

  if (!chart || !rows || rows.length === 0) return null;

  const { x_axis, y_axis } = chart;

  const data = rows.map((row) => ({
    ...row,
    [y_axis]: Number(row[y_axis]) || 0,
  }));

  const handleExportCSV = () => {
    const header = columns.join(",");
    const body = rows.map((row) =>
      columns.map((col) => {
        const val = row[col];
        const str = val === null || val === undefined ? "" : String(val);
        return str.includes(",") || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
      }).join(",")
    ).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `chart_data_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportImage = () => {
    const container = chartRef.current;
    if (!container) return;
    const svg = container.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.scale(2, 2);
      ctx.fillStyle = "#fafafa";
      ctx.fillRect(0, 0, img.width, img.height);
      ctx.drawImage(img, 0, 0);
      const link = document.createElement("a");
      link.download = `chart_${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    const utf8Bytes = new TextEncoder().encode(svgData);
    const binaryStr = Array.from(utf8Bytes, (b) => String.fromCharCode(b)).join("");
    img.src = "data:image/svg+xml;base64," + btoa(binaryStr);
  };

  return (
    <div className="chart-block">
      {/* Toolbar: chart type switcher + export */}
      <div className="chart-toolbar">
        <div className="chart-type-switcher">
          {["bar", "pie", "line"].map((t) => (
            <button
              key={t}
              className={`chart-type-btn ${activeType === t ? "active" : ""}`}
              onClick={() => setActiveType(t)}
              title={`${t.charAt(0).toUpperCase() + t.slice(1)} chart`}
            >
              {CHART_ICONS[t]}
              <span>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
            </button>
          ))}
        </div>
        <div className="chart-export-actions">
          <button className="chart-export-btn" onClick={handleExportCSV} title="Download as CSV">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            CSV
          </button>
          <button className="chart-export-btn" onClick={handleExportImage} title="Download as PNG">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
            </svg>
            PNG
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container" ref={chartRef}>
        {activeType === "pie" ? (
          <div className="pie-chart-wrapper">
            <div className="pie-chart-canvas">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={data}
                    dataKey={y_axis}
                    nameKey={x_axis}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={false}
                    labelLine={false}
                  >
                    {data.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #ddd", borderRadius: 8, color: "#111", fontSize: 13, boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}
                    itemStyle={{ color: "#111" }}
                    labelStyle={{ color: "#111", fontWeight: 600 }}
                    formatter={(value, name) => [value, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <PieLegend data={data} x_axis={x_axis} colors={COLORS} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            {activeType === "bar" ? (
              <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey={x_axis} tick={{ fill: "#aaa", fontSize: 12 }} />
                <YAxis tick={{ fill: "#aaa", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#1e1e1e", border: "1px solid #444", borderRadius: 8, color: "#eee" }} />
                <Legend wrapperStyle={{ color: "#aaa" }} />
                <Bar dataKey={y_axis} radius={[4, 4, 0, 0]}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey={x_axis} tick={{ fill: "#aaa", fontSize: 12 }} />
                <YAxis tick={{ fill: "#aaa", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "#1e1e1e", border: "1px solid #444", borderRadius: 8, color: "#eee" }} />
                <Legend wrapperStyle={{ color: "#aaa" }} />
                <Line type="monotone" dataKey={y_axis} stroke="#6366f1" strokeWidth={2} dot={{ fill: "#6366f1" }} />
              </LineChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default ChartBlock;
